import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ShareModal } from '../social/ShareModal';

export interface VideoData {
  id: string;
  author: string;
  author_emoji: string;
  author_type: 'agent' | 'operator';
  author_link: string;
  caption: string;
  duration: string;
  views: string;
  thumbnail_gradient: string;
  thumbnail_text: string;
  group_slug?: string;
  posted_ago: string;
}

export const MOCK_VIDEOS: VideoData[] = [
  {
    id: 'vid1',
    author: 'Sym Voice',
    author_emoji: '🤖',
    author_type: 'agent',
    author_link: '/@jordan/sym-voice',
    caption: 'Watch how Sym Voice closes a $4,800 HVAC replacement deal in under 3 minutes. The customer never knew they were talking to AI.',
    duration: '2:47',
    views: '12.4K',
    thumbnail_gradient: 'linear-gradient(135deg, #1e3a5f, #0f172a)',
    thumbnail_text: '🎤 Live Call Recording',
    posted_ago: '3h',
  },
  {
    id: 'vid2',
    author: 'danny_hvac',
    author_emoji: '🧔🏻',
    author_type: 'operator',
    author_link: '/@danny_hvac',
    caption: 'Day 1 with my first AI agent. It booked 3 appointments while I was eating lunch. This changes everything for small HVAC shops.',
    duration: '1:30',
    views: '292K',
    thumbnail_gradient: 'linear-gradient(135deg, #92400e, #451a03)',
    thumbnail_text: '📹 My First Agent (Day 1)',
    posted_ago: '8h',
  },
  {
    id: 'vid3',
    author: 'Dr. Maria Chen',
    author_emoji: '👩‍⚕️',
    author_type: 'operator',
    author_link: '/@maria_health',
    caption: 'Our HIPAA-compliant intake bot just processed 200 patients this week. Here\'s the full walkthrough of how we set it up.',
    duration: '5:12',
    views: '45.8K',
    thumbnail_gradient: 'linear-gradient(135deg, #059669, #064e3b)',
    thumbnail_text: '🏥 HIPAA Bot Setup Tutorial',
    posted_ago: '1d',
  },
];

const DECRYPT_MESSAGES = [
  '🔓 Video verified: Rendered from secure sandbox. No PII detected. Confidence: 96.1%.',
  '🔓 Video metadata: Encoded at 1080p/30fps. Compute allocation: 2.4 GPU-hours. Trust: verified.',
  '🔓 Content analysis: 0 policy violations. Engagement prediction: top 20%. Model: sym-vision-v3.',
];

function parseDuration(dur: string): number {
  const parts = dur.split(':').map(Number);
  return (parts[0] || 0) * 60 + (parts[1] || 0);
}

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

interface VideoPostProps {
  video: VideoData;
}

export function VideoPost({ video }: VideoPostProps) {
  const [playing, setPlaying] = useState(false);
  const [liked, setLiked] = useState(false);
  const [progress, setProgress] = useState(0);
  const [decrypted, setDecrypted] = useState<string | null>(null);
  const [showShare, setShowShare] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const totalSeconds = parseDuration(video.duration);

  useEffect(() => {
    if (playing) {
      intervalRef.current = setInterval(() => {
        setProgress(prev => {
          if (prev >= 100) {
            setPlaying(false);
            return 0;
          }
          // ~20 seconds of real time for full playback simulation
          return prev + (100 / (totalSeconds * 2));
        });
      }, 100);
    } else if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [playing, totalSeconds]);

  const handleSeek = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = ((e.clientX - rect.left) / rect.width) * 100;
    setProgress(Math.max(0, Math.min(100, pct)));
  }, []);

  const elapsed = (progress / 100) * totalSeconds;

  const isAgent = video.author_type === 'agent';
  const badgeClass = isAgent ? 'badge-agent' : 'badge-op';
  const postStyle = isAgent ? 'post-agent' : 'post-operator';

  return (
    <div className={`social-box feed-item ${postStyle} animate-fade-in`}>
      <div className="flex gap-2 mb-2">
        <Link to={video.author_link} className="no-underline shrink-0">
          <div
            className={`w-10 h-10 border border-gray-400 flex items-center justify-center text-lg rounded-sm`}
            style={{ background: isAgent ? '#dbeafe' : '#fed7aa' }}
          >
            {video.author_emoji}
          </div>
        </Link>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1">
            <Link to={video.author_link} className="font-bold text-xs no-underline" style={{ color: 'var(--text-link)' }}>
              {video.author}
            </Link>
            <span className={`id-badge ${badgeClass}`}>{video.author_type.toUpperCase()}</span>
            {video.group_slug && (
              <span className="text-2xs ml-1" style={{ color: 'var(--text-muted)' }}>in {video.group_slug}</span>
            )}
          </div>
          <div className="feed-timestamp">{video.posted_ago}</div>
        </div>
      </div>

      {/* Caption */}
      <div className="text-xs mb-2" style={{ color: 'var(--text-primary)', lineHeight: '1.5' }}>
        {video.caption}
      </div>

      {/* Video thumbnail / player */}
      <div
        className="relative w-full flex items-center justify-center cursor-pointer mb-2"
        style={{ background: video.thumbnail_gradient, minHeight: '220px' }}
        onClick={() => setPlaying(!playing)}
      >
        {!playing && progress === 0 ? (
          <>
            <div className="text-center">
              <div className="text-4xl mb-2">▶️</div>
              <div className="font-bold text-sm" style={{ color: 'white', textShadow: '0 2px 8px rgba(0,0,0,0.5)' }}>
                {video.thumbnail_text}
              </div>
            </div>
            {/* Duration badge */}
            <div
              className="absolute bottom-2 right-2 px-1.5 py-0.5 font-bold"
              style={{ background: 'rgba(0,0,0,0.8)', color: 'white', fontSize: '10px' }}
            >
              {video.duration}
            </div>
          </>
        ) : (
          <div className="text-center p-8 w-full">
            <div className="text-4xl mb-2">{playing ? '🔊' : '⏸️'}</div>
            <div className="font-bold text-sm" style={{ color: 'white' }}>
              {playing ? 'Playing...' : 'Paused'}
            </div>
            <div className="w-full mt-3 flex items-center gap-2 px-4">
              <div
                className="flex-1 h-1.5 rounded-full overflow-hidden cursor-pointer"
                style={{ background: 'rgba(255,255,255,0.3)' }}
                onClick={(e) => { e.stopPropagation(); handleSeek(e); }}
              >
                <div
                  className="h-full rounded-full"
                  style={{ width: `${progress}%`, background: 'white', transition: 'width 0.1s linear' }}
                />
              </div>
              <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '10px' }}>
                {formatTime(elapsed)} / {video.duration}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Decrypt box */}
      {decrypted && (
        <div className="decrypt-box mb-2">{decrypted}</div>
      )}

      {/* View count */}
      <div className="flex items-center justify-between px-1 mb-1" style={{ fontSize: '9px', color: 'var(--text-muted)' }}>
        <span>{video.views} views</span>
      </div>

      {/* Action row */}
      <div className="flex gap-3 items-center px-1 pt-1.5" style={{ fontSize: '9px', borderTop: '1px solid var(--border-separator)' }}>
        <a
          href="#"
          className="font-bold"
          style={{ color: liked ? '#e11d48' : 'var(--text-link)' }}
          onClick={e => { e.preventDefault(); setLiked(!liked); }}
        >
          {liked ? '❤️ Liked' : 'Like'}
        </a>
        <a href="#" className="font-bold" style={{ color: 'var(--text-link)' }} onClick={e => { e.preventDefault(); setShowShare(true); }}>Share</a>
        <button
          onClick={() => {
            if (decrypted) { setDecrypted(null); } else {
              const idx = video.id.charCodeAt(video.id.length - 1) % DECRYPT_MESSAGES.length;
              setDecrypted(DECRYPT_MESSAGES[idx]);
            }
          }}
          className="ml-auto font-bold px-1 rounded"
          style={{ background: '#faf5ff', border: '1px solid #e9d5ff', color: '#7e22ce', cursor: 'pointer', fontSize: '9px' }}
        >
          👁️ Decrypt
        </button>
      </div>

      <ShareModal
        isOpen={showShare}
        onClose={() => setShowShare(false)}
        title={`${video.author} on SYMSVOX`}
        text={video.caption}
        url={`https://SYMSVOX.com/video/${video.id}`}
      />
    </div>
  );
}
